import {
  addDisplayName,
  addHandlers,
  addProps,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { addNavigate, addPathParam } from 'shared/src/utils/addNavigate'
import { addEffectOnMount } from 'ad-hok-utils'
import { addAuthContext } from 'shared/src/utils/auth'
import { FC } from 'react'
import { addDataLoadingFromPromise } from 'shared/src/utils/dataLoading'
import { UserWithImages } from 'shared/src/api/types'

import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { ConstrainWidth } from 'utils/layout'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import { TextField } from '../TextField'
import { TextButton } from './TextButton'
import { OmniAuth } from './OmniAuth'
import { Banner } from 'components/banners/Banner'
import { WAITLIST_URL } from 'utils/urls'
import { Avatar } from 'components/Avatar'
import { addLoadingAndErrorIndicators } from 'utils/addLoadingAndErrorIndicators'

const useStyles = createUseStyles({
  container: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 24,
  },
  backButton: {
    background: 'none',
    border: 'none',
    padding: 0,
  },
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 8,
  },
  subtitle: {
    color: '#FFFFFFE0',
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    lineHeight: '187%',
    marginBottom: 12,
    textAlign: 'center',
  },
  grabTheApp: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
  },
  referralBox: {
    color: '#FFFFFFCC',
    fontSize: 16,
    fontFamily: sansSerifFontFamily,
    lineHeight: '140%',
    backgroundColor: '#232323',
    borderRadius: 17,
    padding: '12px 40px 12px 10px',
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
  },
  referralBoxUser: { fontWeight: 'bold', color: 'white' },
  invalid: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.56)',
    marginLeft: 30,
    marginBottom: 0,
  },
  italics: {
    fontStyle: 'italic',
    fontWeight: 600,
  },
  button: {
    height: 64,
    width: '100%',
    marginTop: 48,
    background:
      'radial-gradient(103.73% 266.85% at 0% 16.08%, #CF8355 0%, #CEAF85 38.44%, #9B876D 100%)',
    borderRadius: 2,
    color: '#ffffff',
    '&:disabled': {
      color: 'black',
    },
  },
  linkText: {
    fontWeight: 900,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
})

type Props = {
  grabTheApp?: any
}
export const Referral: FC<Props> = flowMax(
  addDisplayName('Referral'),
  addPathParam('username'),
  addNavigate,
  addApi,
  addAuthContext,
  addTranslationHelpers,
  addStateHandlers(
    (Props) => ({
      email: '',
      grabTheApp: !!Props.grabTheApp,
    }),
    {
      onChangeEmail: () => (newEmail: string) => ({
        email: newEmail,
      }),
      setGrabTheApp: () => () => ({ grabTheApp: true }),
    }
  ),
  addDataLoadingFromPromise(
    'referralUser',
    ({ api, username, grabTheApp }) =>
      grabTheApp
        ? new Promise<UserWithImages | undefined>((r) => {
            r(undefined)
          })
        : api.users.details(username),
    [],
    ({ username }) => `referralUser-${username}`
  ),
  addLoadingAndErrorIndicators('referralUser'),
  addEffectOnMount(({ isAuthenticated, grabTheApp, navigate }) => () => {
    if (isAuthenticated && !grabTheApp) {
      console.error('Logged in')
      navigate('/', { replace: true })
      return
    }
  }),
  addHandlers({
    onContinue:
      ({ api, username, email, setGrabTheApp }) =>
      () => {
        const addReferral = async () => {
          await api.users.referral(username, email).then((response) => {
            console.log('referral: ', response.status)
          })
        }
        addReferral()
        setGrabTheApp()
      },
  }),
  addHandlers({
    onKeyPressContinue:
      ({ onContinue, email }) =>
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && email !== '') {
          onContinue()
        }
      },
  }),
  addProps(() => ({
    classes: useStyles(),
  })),
  ({
    email,
    username,
    referralUser,
    onChangeEmail,
    onContinue,
    onKeyPressContinue,
    classes,
    t,
    grabTheApp,
  }) => (
    <ConstrainWidth fillHeight>
      {grabTheApp ? (
        <div className={classes.grabTheApp}>
          <Banner
            banner={{
              unDismissable: true,
              noHider: true,
              fullHeight: true,
              textTranslationKey: 'banners.mobileOpenDownloadApp.appName',
              onCloseAnalyticsEvent: {
                options: {
                  category: 'general',
                  action: 'banner_open_download_in_app_close',
                },
              },
              link: {
                title: 'download app',
                navigateTo: WAITLIST_URL,
                external: false,
                onClickAnalyticsEvent: {
                  options: {
                    category: 'general',
                    action: 'banner_open_download_in_app_click',
                  },
                },
              },
            }}
            onAction={() => {}}
          />
        </div>
      ) : (
        <div className={classes.container}>
          <>
            <div className={classes.referralBox}>
              {referralUser ? (
                <Avatar creator={referralUser} height={80} />
              ) : (
                <></>
              )}
              <p>
                <span className={classes.referralBoxUser}>{username}</span>{' '}
                invited you to rodeo app
              </p>
            </div>
            <h1 className={classes.title}>
              Register on rod<i>e</i>o
            </h1>
            <p className={classes.subtitle}>
              Join rodeo today to share and discover your style
            </p>
            <OmniAuth
              stateParams={`referral=${username}`}
              style={{ paddingTop: 18 }}
            />
            <p className={classes.subtitle}>Or</p>
            <TextField
              id="email"
              label={t('auth.fields.email')}
              value={email ?? ''}
              onChange={onChangeEmail}
              onKeyPress={onKeyPressContinue}
            />
            {email && !email.match(/^[^@]+@[^@]+\.[^@]+$/) && (
              <p className={classes.invalid}>{t('auth.invalidEmail')}</p>
            )}
            <TextButton
              text="register with email"
              onClick={onContinue}
              disabled={!email || !email.match(/^[^@]+@[^@]+\.[^@]+$/)}
            />
          </>
        </div>
      )}
    </ConstrainWidth>
  )
)
